import type { ViewerProvider, ViewerLaunchParams, ViewerConfig, ViewerInstance, ViewerSyncAdapter } from '../types';
import { FULL_SYNC_CAPABILITIES } from '../types';
import type { BritProtocol } from '../../../api/types';
import { britToOhifProtocol } from '../protocols/britToOhif';
import { sendProtocolToOhif, requestCaptureFromOhif } from '../protocols/ohifBridge';
import type { MonitorChannel } from '../../multi-monitor/MonitorChannel';
import { OHIFSyncAdapter } from '../../multi-monitor/OHIFSyncAdapter';

/**
 * OHIF v3 viewer provider — iframe-based.
 * Generates OHIF viewer URLs, translates protocols to OHIF format,
 * and communicates with the OHIF iframe via postMessage.
 */
export const OHIFViewerProvider: ViewerProvider = {
  id: 'ohif',
  name: 'OHIF Viewer',
  supportsHangingProtocols: true,
  supportsComparisons: true,
  syncCapabilities: FULL_SYNC_CAPABILITIES,

  buildViewerConfig({ study, siteId, sessionId, protocol, comparisonStudyUids }: ViewerLaunchParams): ViewerConfig {
    const params = new URLSearchParams({
      StudyInstanceUIDs: study.uid,
      siteId,
      session: sessionId,
    });

    // Pass comparison studies as additional UIDs
    if (comparisonStudyUids?.length) {
      comparisonStudyUids.forEach(uid => params.append('StudyInstanceUIDs', uid));
    }

    // Pass protocol ID for OHIF to fetch/apply
    if (protocol) {
      params.set('hangingProtocolId', `britpacs-${protocol.id}`);
    }

    return {
      type: 'iframe' as const,
      url: `/ohif/viewer/britpacs?${params.toString()}`,
    };
  },

  translateProtocol(protocol: BritProtocol) {
    return britToOhifProtocol(protocol);
  },

  applyProtocol(viewerInstance: ViewerInstance, protocol: BritProtocol) {
    const iframe = viewerInstance.iframeRef?.current;
    if (!iframe) return;
    sendProtocolToOhif(iframe, protocol);
  },

  async captureProtocol(viewerInstance: ViewerInstance) {
    const iframe = viewerInstance.iframeRef?.current;
    if (!iframe) return null;
    // Request OHIF's current state — returns OHIF format, not Brit format.
    // Phase 4 will add the OHIF→Brit reverse translation.
    const ohifProtocol = await requestCaptureFromOhif(iframe);
    if (!ohifProtocol) return null;
    // TODO: Phase 4 — translate OHIF protocol back to BritProtocol
    return null;
  },

  createSyncAdapter(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ): ViewerSyncAdapter {
    return new OHIFSyncAdapter(channel, windowId, iframeRef);
  },
};
